import React from 'react';
import { Box, TextField } from '@mui/material';
import { FieldValues, useFormContext, Controller, Path, PathValue } from 'react-hook-form';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SxProps } from '@mui/system';
import colors from 'src/utils/colors';

interface StyledSettingProps {
  label?: string | React.ReactNode;
  textSx?: SxProps;
  required?: boolean;
}

type PathControlledTextAreaProps<T extends FieldValues> = StyledSettingProps & {
  name: Path<T>;
  placeholder: string;
  rows?: number;
  characterLimit?: number;
};

const TextAreaInputLabel: React.FC<StyledSettingProps> = ({ textSx, label, required }) => {
  const labelContent =
    typeof label === 'string' ? (
      <>
        {label}
        {required && (
          <Text
            variant="body3"
            sx={{
              lineHeight: '18px',
              marginLeft: '2px'
            }}
          >
            *
          </Text>
        )}
      </>
    ) : (
      <>
        {label}
        {required && (
          <Text
            variant="body3"
            sx={{
              lineHeight: '18px',
              marginLeft: '2px'
            }}
          >
            *
          </Text>
        )}
      </>
    );

  return (
    <Text
      variant="h7"
      sx={{
        ...textSx,
        display: 'flex',
        alignItems: 'center',
        lineHeight: '20px',
        width: '100%'
      }}
    >
      {labelContent}
    </Text>
  );
};

function PathControlledTextArea<T extends FieldValues>({
  name,
  placeholder,
  rows = 4,
  characterLimit,
  label,
  textSx,
  required = false
}: PathControlledTextAreaProps<T>) {
  const { control } = useFormContext<T>();

  return (
    <Box sx={{ width: '100%' }}>
      {label && <TextAreaInputLabel label={label} textSx={textSx} required={required} />}
      <Box
        sx={{
          border: `1px solid ${colors.borderGrey}`,
          borderRadius: '8px',
          backgroundColor: 'white',
          marginTop: label ? '8px' : 0
        }}
      >
        <Controller
          name={name}
          control={control}
          defaultValue={'' as PathValue<T, Path<T>>}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                multiline
                rows={rows}
                placeholder={placeholder}
                variant="outlined"
                fullWidth
                value={field.value || ''}
                onChange={(e) => {
                  if (characterLimit && e.target.value.length > characterLimit) {
                    return; // Don't allow input beyond character limit
                  }

                  field.onChange(e.target.value);
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    border: 'none',
                    borderRadius: '8px',
                    minHeight: rows * 30 + 'px',
                    alignItems: 'flex-start',
                    padding: '16px',
                    fontSize: '14px'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiInputBase-input': {
                    padding: 0,
                    fontSize: '14px',
                    lineHeight: '20px'
                  }
                }}
              />
              {fieldState.error && (
                <Text
                  variant="body3"
                  sx={{
                    color: colors.red,
                    fontSize: '12px',
                    marginTop: '4px'
                  }}
                >
                  {fieldState.error.message}
                </Text>
              )}
            </>
          )}
        />
      </Box>
      {characterLimit && (
        <Text
          variant="body3"
          sx={{
            fontSize: '10px',
            fontWeight: 500,
            color: colors.darkBlue,
            textAlign: 'right',
            marginTop: '4px'
          }}
        >
          {characterLimit} characters remaining
        </Text>
      )}
    </Box>
  );
}

export default PathControlledTextArea;
